import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import JsonLd, { articleSchema } from '../components/JsonLd';

const Example = ({ children }) => (
  <div className="sf-example">
    <div className="sf-example-label">Real-world example</div>
    <p>{children}</p>
  </div>
);

const TradingPrinciples = () => {
  return (
    <>
      <SEO
        title="Trading Principles - Order Types, Chart Patterns & Trading Psychology | StckFuture"
        description="Master trading principles including market orders, limit orders, stop-loss strategies, chart patterns, technical indicators, and trading psychology."
        canonical="https://www.stckfuture.com/trading-principles"
      />
      <JsonLd schema={articleSchema({
        title: 'Trading Principles - Order Types, Chart Patterns & Trading Psychology | StckFuture',
        description: 'Master trading principles including market orders, limit orders, stop-loss strategies, chart patterns, and trading psychology.',
        url: 'https://www.stckfuture.com/trading-principles',
        dateModified: '2026-06-13',
      })} />
      <div className="sf-content-page">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">

              <div className="sf-page-header">
                <span className="sf-eyebrow">Module 3</span>
                <h1>Trading Principles</h1>
                <p className="lead">Learn the mechanics of how trades actually work — order types, timeframes, chart patterns, and the psychology that separates disciplined traders from emotional ones.</p>
              </div>

              {/* Order Types */}
              <div className="sf-card">
                <h3>Order types</h3>
                <p>Every trade starts with an order. Using the right order type for the situation is one of the most practical skills to develop early.</p>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <strong>Market order</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Buy or sell immediately at the current market price. Guarantees execution but not price. Best when speed matters more than getting a specific price.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Limit order</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Buy or sell only at a specific price or better. Gives you price control but no guarantee of execution. Best when you have a target price and can wait.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Stop order (stop-loss)</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Triggers a market order when price reaches a set level. Used to limit losses or protect profits. Once triggered, execution price may vary from the trigger price.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Stop-limit order</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Combines stop and limit — triggers at a price, then executes only at your limit price or better. More precise, but may not fill in fast-moving markets.</p>
                  </div>
                </div>
                <Example>You own Tesla at $200 and it's now trading at $250. You're worried about a downturn. You set a stop-loss at $230 — protecting most of your gains. If Tesla drops to $230, your shares sell automatically. If it keeps rising to $300, the stop order stays inactive and you capture those gains too.</Example>
              </div>

              {/* Trading Psychology */}
              <div className="sf-card">
                <h3>Trading psychology</h3>
                <p>Emotions are the biggest obstacle for most traders. Fear and greed consistently cause people to buy high (chasing rallies) and sell low (panic selling) — the opposite of what works.</p>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <strong>Common pitfalls</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li className="mb-1"><strong>FOMO</strong> — entering trades based on what others are doing</li>
                      <li className="mb-1"><strong>Loss aversion</strong> — holding losers too long hoping for recovery</li>
                      <li className="mb-1"><strong>Confirmation bias</strong> — only seeking info that supports your view</li>
                      <li className="mb-1"><strong>Overconfidence</strong> — taking excess risk after a winning streak</li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <strong>Building discipline</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li className="mb-1"><strong>Trading plan</strong> — write your strategy down and follow it</li>
                      <li className="mb-1"><strong>Trading journal</strong> — record every trade and review patterns</li>
                      <li className="mb-1"><strong>Risk rules</strong> — never risk more than you can afford to lose</li>
                      <li className="mb-1"><strong>Patience</strong> — wait for high-probability setups, don't force trades</li>
                    </ul>
                  </div>
                </div>
                <Example>During the 2020–2021 GameStop short squeeze, many traders bought above $300 due to FOMO — only to watch the stock crash below $50 within weeks. Those who maintained discipline and avoided the hype preserved their capital. The lesson: when a trade is all over the news, the opportunity has usually already passed.</Example>
              </div>

              {/* Technical vs Fundamental */}
              <div className="sf-card">
                <h3>Technical vs fundamental analysis</h3>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <strong>Technical analysis</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Studies price charts, patterns, and indicators to predict future price action. Assumes all known information is already priced in — so the chart tells you everything you need.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Fundamental analysis</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Evaluates a company's intrinsic value via financial statements, earnings, competitive position, and economic factors. More relevant for long-term investing than short-term trading.</p>
                  </div>
                </div>
                <p className="sf-text-small sf-text-muted">Most successful investors use both: fundamentals to identify <em>what</em> to buy, technicals to decide <em>when</em> to buy it.</p>
                <Example>In 2020, Zoom's stock surged from $70 to over $560 as the pandemic drove massive adoption. Technical traders spotted the strong uptrend and momentum signals early. Fundamental analysts identified 355% year-over-year revenue growth. Both approaches pointed to the same opportunity — from different angles.</Example>
              </div>

              {/* Trading Timeframes */}
              <div className="sf-card">
                <h3>Trading timeframes</h3>
                <div className="row g-3">
                  <div className="col-md-6">
                    <strong>Day trading</strong>
                    <p className="sf-text-small sf-text-muted mt-1">All positions opened and closed within the same day. Requires constant monitoring. Uses 1-minute to 1-hour charts. High intensity, high transaction costs.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Swing trading</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Positions held for days to weeks to capture a "swing" in price. Less time-intensive. Uses daily and weekly charts. Combines technical and fundamental signals.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Position trading</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Positions held for months to years. Most similar to long-term investing. Relies heavily on fundamentals and macro trends.</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Scalping</strong>
                    <p className="sf-text-small sf-text-muted mt-1">Ultra-short positions held for seconds to minutes. Targets tiny price movements many times per day. Requires split-second decisions and very low fees.</p>
                  </div>
                </div>
              </div>

              {/* Risk to Reward */}
              <div className="sf-card">
                <h3>Risk-to-reward ratio</h3>
                <p>Before entering any trade, calculate whether the potential reward justifies the risk. The formula:</p>
                <div style={{ background: 'var(--off-white)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '16px 20px', margin: '12px 0 16px', fontFamily: 'monospace', fontSize: '0.9rem' }}>
                  Risk-to-Reward = Potential Loss ÷ Potential Profit
                </div>
                <p className="sf-text-small sf-text-muted">A 1:2 ratio means risking $1 to make $2. Most experienced traders won't take a trade below 1:2, and prefer 1:3 or better. A good ratio means you can be wrong more than half the time and still be profitable overall.</p>
                <Example>A trader spots Apple at $150 with support at $145 and resistance at $165. They buy at $150, set a stop-loss at $144 (risking $6/share) and a take-profit at $162 (targeting $12/share). That's a 1:2 ratio. Even if only 50% of trades with this setup succeed, they profit over time.</Example>
              </div>

              {/* Position Sizing */}
              <div className="sf-card">
                <h3>Position sizing</h3>
                <p>Position sizing determines how much of your account to put into each trade. It's arguably the most important risk management tool you have — even a great strategy fails if you oversize positions.</p>
                <div style={{ background: 'var(--off-white)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '16px 20px', margin: '12px 0 16px', fontFamily: 'monospace', fontSize: '0.9rem' }}>
                  Position Size = (Account Size × Risk %) ÷ (Entry Price − Stop Loss Price)
                </div>
                <Example>A trader with a $100,000 account wants to buy Microsoft at $300 with a stop-loss at $285, risking 1% of their account. Calculation: ($100,000 × 1%) ÷ ($300 − $285) = $1,000 ÷ $15 = 66 shares. This disciplined approach means no single trade can significantly damage the account.</Example>
              </div>

              {/* Chart Patterns */}
              <div className="sf-card">
                <h3>Chart patterns</h3>
                <div className="row g-3">
                  <div className="col-md-6">
                    <strong>Reversal patterns</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li className="mb-1"><strong>Head and shoulders</strong> — three peaks, middle highest; signals potential trend reversal</li>
                      <li className="mb-1"><strong>Double top/bottom</strong> — two peaks or troughs at the same level; signals reversal</li>
                      <li className="mb-1"><strong>Rounding bottom</strong> — gradual "U" shape transition from downtrend to uptrend</li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <strong>Continuation patterns</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li className="mb-1"><strong>Flags and pennants</strong> — brief consolidation before the trend resumes</li>
                      <li className="mb-1"><strong>Triangles</strong> — converging trendlines forming ascending, descending, or symmetric shapes</li>
                      <li className="mb-1"><strong>Cup and handle</strong> — cup-shaped consolidation then a small dip before continuing up</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Trading Journal */}
              <div className="sf-card">
                <h3>Trading journal</h3>
                <p>Keeping a detailed record of every trade is how traders actually improve over time. Without a journal, you repeat the same mistakes without realising it.</p>
                <div className="row g-3">
                  <div className="col-md-6">
                    <strong>What to record per trade</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li>Date, time, and symbol</li>
                      <li>Entry and exit prices</li>
                      <li>Position size and strategy used</li>
                      <li>Profit/loss and why you exited</li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <strong>Metrics to track over time</strong>
                    <ul className="sf-text-small sf-text-muted mt-2">
                      <li><strong>Win rate</strong> — % of winning trades</li>
                      <li><strong>Average R:R</strong> — average profit vs average loss</li>
                      <li><strong>Max drawdown</strong> — largest peak-to-trough decline</li>
                      <li><strong>Profit factor</strong> — gross profits ÷ gross losses</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Key Takeaways */}
              <div className="sf-takeaways">
                <h3>Key takeaways</h3>
                <ul>
                  <li>Use the right order type for each situation — market for speed, limit for price control, stop-loss for protection</li>
                  <li>Managing emotions is often more important than market knowledge — discipline beats intelligence in trading</li>
                  <li>Always calculate the risk-to-reward ratio before entering a trade; aim for at least 1:2</li>
                  <li>Size positions to limit each trade to 1–2% of your total account — no single trade should threaten the whole account</li>
                  <li>Chart patterns help identify setups; they're signals, not guarantees</li>
                  <li>Keep a trading journal — it's the fastest path to identifying and fixing your own patterns</li>
                  <li>Have a written trading plan and follow it regardless of how you feel in the moment</li>
                </ul>
              </div>

              <div className="sf-next-page">
                <Link to="/risk-management" className="btn-sf-primary">
                  Next: Risk Management →
                </Link>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TradingPrinciples;
